import { isPlatformBrowser } from '@angular/common';
import { AfterViewChecked, Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { ButtonItem, FilterTypes } from '@next/core-lib/components';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { SelectedCustomer, SelectedLocation, UserActions, UserRelationshipType, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil, withLatestFrom } from 'rxjs/operators';

import { Environment } from 'src/environment';

import * as OverviewModels from './models';
import { FertiliserOrderplan } from './models';
import { CropFieldLinkTypesEnum } from '../shared/models';
import * as OverviewActions from '../store/actions';
import * as OverviewSelectors from '../store/selectors';
import { CreateOrderReceiptReport, GetArticlesByCropAids, GetFertiliserOrderplan, ConfirmOrderForOverview } from './store/actions/fertilisers-actions';
import { selectFertiliserOrderplan, selectNonOrderableFertilisers, selectProprietaryFertilisers } from './store/selectors/fertiliser.selectors';

enum ButtonAction {
  linkCropFields = 'linkCropFields',
}

@Component({
  selector: 'app-fertilisers-overview',
  templateUrl: './fertilisers-overview.component.html',
  styleUrls: ['./fertilisers-overview.component.scss'],
  standalone: false,
})
export class FertilisersOverviewComponent implements OnInit, OnDestroy, AfterViewChecked {
  readonly env = inject<Environment>(EnvironmentInjector);
  private readonly platformId = inject(PLATFORM_ID);
  readonly locale = inject(LocaleService);
  private readonly store = inject<Store<SiteState>>(Store);

  columnsToDisplay: string[] = [...OverviewModels.displayColumns];
  expandedElement: FertiliserOrderplan | null;
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  customer: SelectedCustomer;
  location$ = this.store.pipe(select(UserSelectors.selectLocation));
  location: SelectedLocation;
  orderLineStatus = OverviewModels.OrderLineStatus;
  fertiliserOrderplan$ = this.store.pipe(select(selectFertiliserOrderplan));
  nonOrderableFertilisers$ = this.store.pipe(select(selectNonOrderableFertilisers));
  proprietaryFertilisers$ = this.store.pipe(select(selectProprietaryFertilisers));
  cropYear$ = this.store.pipe(select(OverviewSelectors.getYear));
  defaultCropYear$ = this.store.pipe(select(OverviewSelectors.selectDefaultYear));
  availableCropYears$ = this.store.pipe(select(OverviewSelectors.getAvailableYears));
  cropFields$ = this.store.pipe(select(OverviewSelectors.selectCropFields));
  areAnyCropFieldsLinked = false;
  areAnyCropFieldsNotLinked = false;
  cropYearChanged$ = new Subject<number>();
  clearTableSelections$: Subject<boolean> = new Subject();
  isPrimaryActionDisabled = false;
  filterConfig = {
    filters: {
      year: {
        label: 'year',
        type: FilterTypes.Select,
        options: this.availableCropYears$.pipe(
          map((availableYears) =>
            availableYears.map((availableYear) => ({
              value: availableYear,
              viewValue: availableYear.toString(),
            })),
          ),
        ),
        value: this.cropYear$,
        defaultValue: this.defaultCropYear$,
        changes: this.cropYearChanged$,
      },
    },
  };

  buttonItems: ButtonItem[] = [];

  private readonly destroyed$ = new Subject<boolean>();
  ngAfterViewChecked(): void {
    if (isPlatformBrowser(this.platformId)) {
      //Set full width for sticky header
      const stickyWrapper = document.getElementsByClassName('sticky__wrapper') as HTMLCollectionOf<HTMLElement>;
      if (stickyWrapper && stickyWrapper.length > 0) {
        stickyWrapper[0].style.width = '100%';
      }
      const stickyContainer = document.getElementsByClassName('sticky__container') as HTMLCollectionOf<HTMLElement>;
      if (stickyContainer && stickyContainer.length > 0) {
        stickyContainer[0].style.width = '100%';
      }
    }
  }

  ngOnInit() {
    this.cropYearChanged$.pipe(withLatestFrom(this.cropYear$), takeUntil(this.destroyed$)).subscribe(([newYear, existingYear]) => {
      if (newYear && newYear !== existingYear) {
        this.store.dispatch(OverviewActions.resetYear({ year: newYear }));
      }
    });

    combineLatest([this.location$, this.cropFields$]).subscribe(([location, cropFields]) => {
      if (location && cropFields) {
        this.areAnyCropFieldsLinked = cropFields.some((c) => c.fertilizerShipToId === location?.id);
      }
      if (cropFields) {
        this.areAnyCropFieldsNotLinked = cropFields.filter((c) => !c.fertilizerShipToId).length > 0;
      }
    });

    this.customer$.subscribe((customer) => {
      if (!customer || customer?.id !== this.customer?.id) {
        this.location = null;
      }

      this.customer = customer;
    });

    combineLatest([this.location$])
      .pipe(withLatestFrom(this.store.pipe(select(UserSelectors.selectLocations), takeUntil(this.destroyed$))))
      .subscribe(([[location], availableLocations]) => {
        if (location) {
          this.location = location;
        } else if (this.customer) {
          if (!this.location && availableLocations.length === 1) {
            this.location = availableLocations[0];
          }

          if (this.location) {
            this.store.dispatch(
              UserActions.relationshipSelected({
                relatedType: UserRelationshipType.Location,
                customerId: this.customer.id,
                locationId: this.location.id,
              }),
            );
          }
        }

        this.buttonItems = this.getButtonItems(!!this.location);
      });

    this.getFertiliserOrderplan();
    this.fertiliserOrderplan$.pipe(takeUntil(this.destroyed$)).subscribe((orderableFertilisers) => {
      if (orderableFertilisers && orderableFertilisers.length > 0) {
        this.store.dispatch(new GetArticlesByCropAids());
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buttonAction(data: string) {
    switch (data) {
      case ButtonAction.linkCropFields:
        this.linkCropFields();
        break;
    }
  }

  doEmptyAction() {
    if (this.areAnyCropFieldsLinked) {
      this.openFieldAdvice();
    } else {
      this.linkCropFields();
    }
  }

  openFieldAdvice() {
    this.store.dispatch(OverviewActions.openFieldAdvice());
  }

  linkCropFields() {
    this.store.dispatch(OverviewActions.linkCropFields({ cropFieldLinkType: CropFieldLinkTypesEnum.fertilizer }));
  }

  createOrderReceiptReport() {
    this.store.dispatch(new CreateOrderReceiptReport());
  }

  confirmOrder() {
    this.store.dispatch(new ConfirmOrderForOverview());
  }

  onTogglePrimaryActionTableButton(eventValue: boolean) {
    this.isPrimaryActionDisabled = eventValue;
  }

  private readonly getButtonItems = (hasLocation: boolean): ButtonItem[] => {
    const buttonItems: ButtonItem[] = [];

    if (hasLocation) {
      buttonItems.push(
        ...[
          {
            icon: 'link',
            viewValue: 'linkCropFields',
            key: ButtonAction.linkCropFields,
          },
        ],
      );
    }

    return buttonItems;
  };

  private getFertiliserOrderplan() {
    this.store.dispatch(new GetFertiliserOrderplan());
  }
}
